import { JSX } from "react";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import { appContext } from "../../../../AppContext";
import Helper from "../../../../Common/Helper";
import { SerendieSymbol } from "@serendie/symbols";
import { PagedResponse } from "@iconics/core";
import { OntologyNodeParameter, SolutionParameter } from "./DataModels";
import EditOntologyNodeParameterDialog from "./EditOntologyNodeParameterDialog";
import { IOntologyNode } from "./OntologyNodesCard";
import { Guid } from "@iconics/core";
import { Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, OnSectionUpdateHandler } from "@iconics/components";

export default class OntologyNodeParametersCard extends IbssComponent<IProps, IState>
{
    constructor(props: IProps)
    {
        const { localStorageProvider } = appContext();

        super(props);
        this.state = {
            userCanUpdateParameters: localStorageProvider.hasRight('DATAMODEL.Parameters.Update'),
            gridData: { rows: [] },
            gridRefreshToken: Guid.new().toString(),
            showEditNodeParameterDialog: false,
        };
    }

    public componentDidUpdate(prevProps: IProps): void
    {
        const prevOntologyNodesHash = prevProps.ontologyNodes.map(i => i.id).sort().join(',');
        const ontologyNodesHash = this.props.ontologyNodes.map(i => i.id).sort().join(',');

        if (prevOntologyNodesHash != ontologyNodesHash)
        {
            this.setState({
                ontologyNodeParameters: undefined,
                gridRefreshToken: Guid.new().toString(),
            });
        }
    }

    private handleEditParameter(ontologyNodeParameter: IOntologyNodeParameter): void
    {
        if (!this.state.userCanUpdateParameters)
        {
            return;
        }

        this.setState({
            showEditNodeParameterDialog: true,
            selectedOntologyNodeParameter: ontologyNodeParameter
        });
    }

    private handleSaveOntologyNodeParameter(): void
    {
        this.setState({
            showEditNodeParameterDialog: false,
            ontologyNodeParameters: undefined,
            gridRefreshToken: Guid.new().toString(),
        });
    }

    private async handleRowsRequest(request: IGridRowsRequest<IOntologyNodeParameter>): Promise<IGridRowsResponse<IOntologyNodeParameter>>
    {
        if (!this.state.ontologyNodeParameters)
        {
            let ontologyNodeParameters: IOntologyNodeParameter[] = [];
            for (const ontologyNode of this.props.ontologyNodes)
            {
                const parametersData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.ontologyNodes.byOntologyNodeId.parameters.get<PagedResponse<OntologyNodeParameter[]>>({
                    tenancy: appContext().tenantId,
                    nodeId: this.props.nodeId,
                    ontologyNodeId: ontologyNode.id,
                });

                for (const parameterData of parametersData.value)
                {
                    const singleParameterData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.parameters.byParameterId.get<SolutionParameter>({
                        tenancy: appContext().tenantId,
                        id: parameterData.Solution_ID,
                        parameterId: parameterData.Parameter_ID,
                    });

                    ontologyNodeParameters.push({
                        id: parameterData.Id,
                        name: singleParameterData.Name,
                        isEnabled: parameterData.IsEnabled === 'Yes',
                        value: parameterData.Value,
                        valueType: parameterData.ValueType,
                        ontologyName: ontologyNode.ontologyName,
                        ontologyNodeName: ontologyNode.ontologyNodeName,
                        ontologyNodeId: ontologyNode.id,
                        concurrencyStamp: parameterData["@odata.etag"],

                    } satisfies IOntologyNodeParameter);
                }

            }
            await this.setStateAsync({ ontologyNodeParameters: ontologyNodeParameters });
        }
        return {
            rows: this.state.ontologyNodeParameters ?? [],
            nextSkipToken: '',
        };
    }

    public render(): JSX.Element
    {
        const { labels } = appContext();

        return (
            <>
                <Card
                    title={labels.Platform_Node_MetaDataListTitle}
                    subTitle={labels.Platform_Node_MetaDataListSubTitle}
                    onSectionUpdate={i => this.props.onSectionUpdate(i)}
                >
                    <GridContainer>
                        <GridItem>
                            <DataGrid
                                columns={[
                                    {
                                        columnId: Helper.nameOf<IOntologyNodeParameter>("name"),
                                        title: labels.Parameter_Name,
                                        getCellValue: i => [i.ontologyName, i.ontologyNodeName, i.name].join('\\'),
                                        columnWidth: 50
                                    },
                                    {
                                        columnId: Helper.nameOf<IOntologyNodeParameter>("isEnabled"),
                                        title: labels.Parameter_IsEnabled,
                                        getCellValue: i =>
                                            <SerendieSymbol
                                                name={i.isEnabled ? 'check-circle' : 'close-circle'}
                                                style={{ marginLeft: '32px' }}
                                            />,
                                        columnWidth: 15
                                    },
                                    {
                                        columnId: Helper.nameOf<IOntologyNodeParameter>("valueType"),
                                        title: labels.Parameter_ValueType,
                                        getCellValue: i => i.valueType,
                                        columnWidth: 15
                                    },
                                    {
                                        columnId: Helper.nameOf<IOntologyNodeParameter>("value"),
                                        title: labels.Parameter_Value,
                                        getCellValue: i => i.value,
                                        columnWidth: 20
                                    },
                                ]}
                                data={this.state.gridData}
                                onChange={i => this.setState({ gridData: i })}
                                rowId={i => i.id}
                                onRowsRequest={i => this.handleRowsRequest(i)}
                                onCellClicked={i => this.handleEditParameter(i)}
                                refreshToken={this.state.gridRefreshToken}
                            />
                        </GridItem>
                    </GridContainer>
                </Card>
                <EditOntologyNodeParameterDialog
                    open={this.state.showEditNodeParameterDialog}
                    onClose={() => this.setState({ showEditNodeParameterDialog: false })}
                    ontologyNodeParameter={this.state.selectedOntologyNodeParameter}
                    onSave={() => this.handleSaveOntologyNodeParameter()}
                    nodeId={this.props.nodeId}
                    tenancy={appContext().tenantId}
                />
            </>
        );
    }
}

export interface IProps
{
    nodeId: number;
    ontologyNodes: IOntologyNode[];
    onSectionUpdate: OnSectionUpdateHandler;
}

export interface IState
{
    userCanUpdateParameters: boolean;
    gridData: IGridData<IOntologyNodeParameter>;
    gridRefreshToken: string;
    ontologyNodeParameters?: IOntologyNodeParameter[];
    selectedOntologyNodeParameter?: IOntologyNodeParameter;
    showEditNodeParameterDialog: boolean;
}

export interface IOntologyNodeParameter
{
    id: string;
    ontologyName: string;
    ontologyNodeName: string;
    ontologyNodeId: string;
    name: string;
    isEnabled: boolean;
    valueType: string;
    value: string;
    concurrencyStamp: string;
}
